import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_Buttons() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔘 Advanced CSS: Buttons</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Buttons are a crucial part of user interfaces. With CSS, we can go
        beyond simple rectangular buttons and create modern, animated, and
        interactive button styles using gradients, shadows, transitions, and
        pseudo-elements.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>box-shadow</code> for depth and 3D effects.
          </li>
          <li>
            Apply <code>linear-gradient</code> backgrounds for modern looks.
          </li>
          <li>
            Add <code>transition</code> or <code>transform</code> for smooth
            hover animations.
          </li>
          <li>
            Utilize <code>::before</code> and <code>::after</code> for creative
            hover effects.
          </li>
          <li>
            Rounded corners and outline styles improve accessibility and modern
            design.
          </li>
        </ul>
      </div>

      {/* Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Button Examples</h2>

        {/* Gradient Button */}
        <h3>1. Gradient Button</h3>
        <CodeCopyBlock
          code={`button {
  background: linear-gradient(90deg, #3b82f6, #9333ea);
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: transform 0.3s ease;
}
button:hover {
  transform: scale(1.05);
}`}
        />
        <button
          style={{
            background: "linear-gradient(90deg, #3b82f6, #9333ea)",
            border: "none",
            padding: "12px 24px",
            borderRadius: "8px",
            color: "white",
            fontSize: "16px",
            cursor: "pointer",
            transition: "transform 0.3s ease",
            marginBottom: "1rem",
          }}
          onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
          onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          Gradient Button
        </button>

        {/* Outline Glow Button */}
        <h3>2. Outline Glow Button</h3>
        <CodeCopyBlock
          code={`button {
  background: transparent;
  border: 2px solid #10b981;
  padding: 12px 24px;
  border-radius: 8px;
  color: #10b981;
  font-size: 16px;
  cursor: pointer;
  transition: box-shadow 0.3s ease, color 0.3s ease;
}
button:hover {
  color: white;
  background: #10b981;
  box-shadow: 0 0 15px #10b981;
}`}
        />
        <button
          style={{
            background: "transparent",
            border: "2px solid #10b981",
            padding: "12px 24px",
            borderRadius: "8px",
            color: "#10b981",
            fontSize: "16px",
            cursor: "pointer",
            transition: "all 0.3s ease",
            marginBottom: "1rem",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.color = "white";
            e.currentTarget.style.background = "#10b981";
            e.currentTarget.style.boxShadow = "0 0 15px #10b981";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.color = "#10b981";
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          Outline Glow
        </button>

        {/* Sliding Background Button */}
        <h3>3. Sliding Background Button</h3>
        <CodeCopyBlock
          code={`button {
  position: relative;
  overflow: hidden;
  border: 2px solid #f59e0b;
  padding: 12px 24px;
  border-radius: 8px;
  background: transparent;
  color: #f59e0b;
  cursor: pointer;
  transition: color 0.3s ease;
}
button::before {
  content: "";
  position: absolute;
  top: 0; left: -100%;
  width: 100%; height: 100%;
  background: #f59e0b;
  z-index: -1;
  transition: left 0.3s ease;
}
button:hover {
  color: white;
}
button:hover::before {
  left: 0;
}`}
        />
        <button
          style={{
            position: "relative",
            overflow: "hidden",
            border: "2px solid #f59e0b",
            padding: "12px 24px",
            borderRadius: "8px",
            background: "transparent",
            color: "#f59e0b",
            cursor: "pointer",
          }}
          className="sliding-btn"
        >
          Sliding Background
        </button>
      </div>

      {/* 2. Neon Glow */}
      <h3>4. Neon Glow</h3>
      <CodeCopyBlock
        code={`button {
  background: #111;
  color: #0ff;
  border: 2px solid #0ff;
  box-shadow: 0 0 10px #0ff, 0 0 20px #0ff;
}`}
      />
      <button
        style={{
          background: "#111",
          color: "#0ff",
          padding: "12px 24px",
          border: "2px solid #0ff",
          borderRadius: "6px",
          boxShadow: "0 0 10px #0ff,0 0 20px #0ff",
        }}
      >
        Neon Glow
      </button>

      {/* 3. Pastel Button */}
      <h3>5. Pastel Button</h3>
      <CodeCopyBlock
        code={`button {
  background: #fde68a;
  color: #92400e;
  border-radius: 12px;
}`}
      />
      <button
        style={{
          background: "#fde68a",
          color: "#92400e",
          padding: "12px 24px",
          border: "none",
          borderRadius: "12px",
        }}
      >
        Pastel
      </button>

      {/* 4. Rainbow Gradient */}
      <h3>6. Rainbow Gradient</h3>
      <CodeCopyBlock
        code={`button {
  background: linear-gradient(45deg, red, orange, yellow, green, blue, indigo, violet);
}`}
      />
      <button
        style={{
          background:
            "linear-gradient(45deg,red,orange,yellow,green,blue,indigo,violet)",
          color: "white",
          padding: "12px 24px",
          border: "none",
          borderRadius: "6px",
        }}
      >
        Rainbow
      </button>

      {/* SECTION 2: 3D & Shadow */}
      <h2 className="section-title">🌌 2. 3D & Shadow Buttons</h2>

      {/* 5. Raised Shadow */}
      <h3>7. Raised Shadow</h3>
      <CodeCopyBlock
        code={`button {
  background: #3b82f6;
  color: white;
  box-shadow: 0 4px 8px rgba(0,0,0,0.3);
}`}
      />
      <button
        style={{
          background: "#3b82f6",
          color: "white",
          padding: "12px 24px",
          border: "none",
          borderRadius: "6px",
          boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
        }}
      >
        Raised
      </button>

      {/* 6. Inset Pressed */}
      <h3>8. Pressed Inset</h3>
      <CodeCopyBlock
        code={`button {
  background: #10b981;
  box-shadow: inset 3px 3px 6px rgba(0,0,0,0.3);
}`}
      />
      <button
        style={{
          background: "#10b981",
          color: "white",
          padding: "12px 24px",
          border: "none",
          borderRadius: "6px",
          boxShadow: "inset 3px 3px 6px rgba(0,0,0,0.3)",
        }}
      >
        Pressed
      </button>

      {/* 7. Neumorphism */}
      <h3>9. Neumorphism</h3>
      <CodeCopyBlock
        code={`button {
  background: #e0e0e0;
  box-shadow: 6px 6px 12px #bebebe, -6px -6px 12px #ffffff;
}`}
      />
      <button
        style={{
          background: "#e0e0e0",
          padding: "12px 24px",
          border: "none",
          borderRadius: "12px",
          boxShadow: "6px 6px 12px #bebebe,-6px -6px 12px #fff",
        }}
      >
        Neumorphism
      </button>

      {/* 8. Glassmorphism */}
      <h3>10. Glassmorphism</h3>
      <CodeCopyBlock
        code={`button {
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.3);
  backdrop-filter: blur(10px);
}`}
      />
      <button
        style={{
          background: "rgba(255,255,255,0.1)",
          padding: "12px 24px",
          border: "1px solid rgba(255,255,255,0.3)",
          borderRadius: "12px",
          color: "white",
          backdropFilter: "blur(10px)",
        }}
      >
        Glass
      </button>

      {/* SECTION 3: Outline & Border */}
      <h2 className="section-title">✒️ 3. Outline & Border Buttons</h2>

      {/* 11. Outline Glow */}
      <h3>11. Outline Glow</h3>
      <CodeCopyBlock
        code={`button {
  background: transparent;
  border: 2px solid #f59e0b;
  padding: 12px 24px;
  border-radius: 6px;
  color: #f59e0b;
}`}
      />
      <button
        style={{
          background: "transparent",
          border: "2px solid #f59e0b",
          padding: "12px 24px",
          borderRadius: "6px",
          color: "#f59e0b",
        }}
      >
        Outline Glow
      </button>

      {/* 12. Dashed Border */}
      <h3>12. Dashed Border</h3>
      <CodeCopyBlock
        code={`button {
  background: transparent;
  border: 2px dashed #3b82f6;
  padding: 12px 24px;
  color: #3b82f6;
}`}
      />
      <button
        style={{
          background: "transparent",
          border: "2px dashed #3b82f6",
          padding: "12px 24px",
          color: "#3b82f6",
        }}
      >
        Dashed
      </button>

      {/* 13. Double Border */}
      <h3>13. Double Border</h3>
      <CodeCopyBlock
        code={`button {
  background: transparent;
  border: 4px double #9333ea;
  padding: 12px 24px;
  color: #9333ea;
}`}
      />
      <button
        style={{
          background: "transparent",
          border: "4px double #9333ea",
          padding: "12px 24px",
          color: "#9333ea",
        }}
      >
        Double Border
      </button>

      {/* 14. Rounded Pill */}
      <h3>14. Rounded Pill</h3>
      <CodeCopyBlock
        code={`button {
  background: #10b981;
  color: white;
  padding: 12px 32px;
  border: none;
  border-radius: 999px;
}`}
      />
      <button
        style={{
          background: "#10b981",
          color: "white",
          padding: "12px 32px",
          border: "none",
          borderRadius: "999px",
        }}
      >
        Rounded Pill
      </button>

      {/* 15. Scale on Hover */}
      <h3>15. Scale on Hover</h3>
      <CodeCopyBlock
        code={`button {
  background: #3b82f6;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  transition: transform 0.3s;
}
button:hover {
  transform: scale(1.1);
}`}
      />
      <button
        style={{
          background: "#3b82f6",
          color: "white",
          padding: "12px 24px",
          border: "none",
          borderRadius: "6px",
          transition: "transform 0.3s",
        }}
        onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
        onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        Scale Hover
      </button>

      {/* 16. Rotate on Hover */}
      <h3>16. Rotate on Hover</h3>
      <CodeCopyBlock
        code={`button {
  background: #f59e0b;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  transition: transform 0.3s;
}
button:hover {
  transform: rotate(5deg);
}`}
      />
      <button
        style={{
          background: "#f59e0b",
          color: "white",
          padding: "12px 24px",
          border: "none",
          borderRadius: "6px",
          transition: "transform 0.3s",
        }}
        onMouseOver={(e) => (e.currentTarget.style.transform = "rotate(5deg)")}
        onMouseOut={(e) => (e.currentTarget.style.transform = "rotate(0deg)")}
      >
        Rotate Hover
      </button>

      {/* 17. Gradient Hover Change */}
      <h3>17. Gradient Hover Change</h3>
      <CodeCopyBlock
        code={`button {
  background: linear-gradient(90deg,#f43f5e,#f97316);
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  transition: background 0.5s;
}
button:hover {
  background: linear-gradient(90deg,#16a34a,#06b6d4);
}`}
      />
      <button
        style={{
          background: "linear-gradient(90deg,#f43f5e,#f97316)",
          color: "white",
          padding: "12px 24px",
          border: "none",
          borderRadius: "6px",
          transition: "background 0.5s",
        }}
        onMouseOver={(e) =>
          (e.currentTarget.style.background =
            "linear-gradient(90deg,#16a34a,#06b6d4)")
        }
        onMouseOut={(e) =>
          (e.currentTarget.style.background =
            "linear-gradient(90deg,#f43f5e,#f97316)")
        }
      >
        Hover Gradient
      </button>

      {/* 18. Icon Button */}
      <h3>18. Icon Button</h3>
      <CodeCopyBlock
        code={`button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #3b82f6;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
}`}
      />
      <button
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          background: "#3b82f6",
          color: "white",
          padding: "12px 24px",
          border: "none",
          borderRadius: "6px",
        }}
      >
        ⭐ Star
      </button>

      {/* 19. Shadow Pop */}
      <h3>19. Shadow Pop</h3>
      <CodeCopyBlock
        code={`button {
  background: #9333ea;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  transition: all 0.2s;
}
button:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 12px rgba(0,0,0,0.3);
}`}
      />
      <button
        style={{
          background: "#9333ea",
          color: "white",
          padding: "12px 24px",
          border: "none",
          borderRadius: "6px",
          transition: "all 0.2s",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 6px 12px rgba(0,0,0,0.3)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        Shadow Pop
      </button>

      {/* 20. Gradient Border */}
      <h3>20. Gradient Border</h3>
      <CodeCopyBlock
        code={`button {
  background: transparent;
  padding: 12px 24px;
  border: 3px solid transparent;
  border-image: linear-gradient(90deg,#3b82f6,#9333ea) 1;
  color: #3b82f6;
}`}
      />
      <button
        style={{
          background: "transparent",
          padding: "12px 24px",
          border: "3px solid transparent",
          borderImage: "linear-gradient(90deg,#3b82f6,#9333ea) 1",
          color: "#3b82f6",
        }}
      >
        Gradient Border
      </button>

      {/* 21. Glow Pulse */}
      <h3>21. Glow Pulse</h3>
      <CodeCopyBlock
        code={`@keyframes pulse {
  0%, 100% { box-shadow: 0 0 10px #ef4444; }
  50% { box-shadow: 0 0 20px #ef4444; }
}
button {
  background: #ef4444;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  animation: pulse 1.5s infinite;
}`}
      />
      <button
        style={{
          background: "#ef4444",
          color: "white",
          padding: "12px 24px",
          border: "none",
          borderRadius: "6px",
          animation: "pulse 1.5s infinite",
        }}
      >
        Glow Pulse
      </button>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Experiment with <code>clip-path</code> for creative button shapes.
          </li>
          <li>
            Use <code>keyframes</code> for animated gradients or pulsing
            effects.
          </li>
          <li>
            Combine <code>box-shadow</code> and <code>transform</code> for
            3D-style buttons.
          </li>
          <li>
            Explore <code>CSS variables</code> for themeable button styles.
          </li>
          <li>Test accessibility: ensure good contrast and focus outlines.</li>
        </ul>
      </div>
    </div>
  );
}
